import type { Weather, WeatherSnapshot } from "@/adapters/api";
import type { HourForecast, WeatherView } from "../types.ts";
import { formatHour, isWorkable, uvLabel } from "./derive.ts";
import {
	weatherCodeToCondition,
	weatherCodeToDescription,
} from "./weather-code.ts";

const HOURLY_STRIP_SIZE = 5;

function round(value: number | null): number | null {
	return value === null ? null : Math.round(value);
}

export function toWeatherView(weather: Weather): WeatherView {
	const { current, hourly, utc_offset_seconds: utcOffsetSeconds } = weather;

	const upcoming = [...hourly].sort((a, b) =>
		a.bucket_time.localeCompare(b.bucket_time),
	);

	const currentHourProbability = upcoming[0]?.precipitation_probability ?? null;
	const precipProbability =
		current.precipitation_probability ?? currentHourProbability;

	const strip: HourForecast[] = upcoming
		.slice(0, HOURLY_STRIP_SIZE)
		.map((h) => ({
			bucketTime: h.bucket_time,
			time: formatHour(h.bucket_time, utcOffsetSeconds),
			condition: weatherCodeToCondition(h.weather_code),
			temp: round(h.temperature_2m),
			workable: isWorkable(h),
		}));

	const uv = round(current.uv_index);

	return {
		condition: weatherCodeToCondition(current.weather_code),
		temp: round(current.temperature_2m),
		description: weatherCodeToDescription(current.weather_code),
		workable: isWorkable(current, currentHourProbability),
		humidity: round(current.relative_humidity_2m),
		precipProbability: round(precipProbability),
		uvIndex: uv,
		uvLabel: uvLabel(uv),
		hourly: strip,
		alert: buildAlert(current, currentHourProbability, strip),
	};
}

function buildAlert(
	current: WeatherSnapshot,
	currentHourProbability: number | null,
	strip: HourForecast[],
): string | null {
	if (!isWorkable(current, currentHourProbability)) {
		return "Condições de risco agora — avalie atividades externas.";
	}
	const firstRisk = strip.find((h) => !h.workable);
	if (firstRisk) {
		return `Chuva prevista por volta das ${firstRisk.time} — antecipe atividades externas.`;
	}
	return null;
}
